import React, { useEffect, useState, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { API_BASE_URL } from "../../../environment";
import {
  showErrorAlert,
  showSuccessAlert,
  showInfoAlert,
} from "../../uiInterface/base-ui/alertservice";
import { AuthContext } from "../../../context/authcontext";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import CompanyLogo from "../../../assets/company.png";
import { CurrencySelect } from "../../../components/currency";
import { storeCurrencyCode } from "../../../components/currencyFormat";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";

const OrganizationLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading organization settings...</span>
  </div>
);

const CompanySettings = () => {
  const route = all_routes;
  const { selectedTenantId, setAuth, auth } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);

  const currentUserRole = useMemo(() => {
    if (!auth?.tenants || !selectedTenantId) return null;
    const currentTenant = auth.tenants.find(
      (t: any) => String(t.tenant_id) === String(selectedTenantId)
    );
    return currentTenant?.role_key || null;
  }, [auth?.tenants, selectedTenantId]);

  const DOMAIN_REGEX =
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

  const [orgName, setOrgName] = useState("");
  const [domain, setDomain] = useState("");
  const [domainError, setDomainError] = useState("");
  const [currencyCode, setCurrencyCode] = useState("");
  const [initialOrgName, setInitialOrgName] = useState("");
  const [initialDomain, setInitialDomain] = useState("");
  const [initialCurrencyCode, setInitialCurrencyCode] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [logoPreview, setLogoPreview] = useState<string>("");

  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [initialAddressLine1, setInitialAddressLine1] = useState("");
  const [initialAddressLine2, setInitialAddressLine2] = useState("");
  const [initialCity, setInitialCity] = useState("");
  const [initialState, setInitialState] = useState("");
  const [initialCountry, setInitialCountry] = useState("");
  const [initialZipCode, setInitialZipCode] = useState("");
  const [initialLogoUrl, setInitialLogoUrl] = useState("");

  const [isServiceTax, setIsServiceTax] = useState(false);
  const [isGstTax, setIsGstTax] = useState(false);
  const [isVatTax, setIsVatTax] = useState(false);
  const [serviceTaxPercentage, setServiceTaxPercentage] = useState(0);
  const [gstTaxPercentage, setGstTaxPercentage] = useState(0);
  const [vatTaxPercentage, setVatTaxPercentage] = useState(0);
  const [initialIsServiceTax, setInitialIsServiceTax] = useState(false);
  const [initialIsGstTax, setInitialIsGstTax] = useState(false);
  const [initialIsVatTax, setInitialIsVatTax] = useState(false);
  const [initialServiceTaxPercentage, setInitialServiceTaxPercentage] =
    useState(0);
  const [initialGstTaxPercentage, setInitialGstTaxPercentage] = useState(0);
  const [initialVatTaxPercentage, setInitialVatTaxPercentage] = useState(0);

  const [tenantPhone, setTenantPhone] = useState("");
  const [tenantEmail, setTenantEmail] = useState("");
  const [tenantTaxId, setTenantTaxId] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [footerText, setFooterText] = useState("");
  const [initialTenantPhone, setInitialTenantPhone] = useState("");
  const [initialTenantEmail, setInitialTenantEmail] = useState("");
  const [initialTenantTaxId, setInitialTenantTaxId] = useState("");
  const [initialTermsAndConditions, setInitialTermsAndConditions] =
    useState("");
  const [initialFooterText, setInitialFooterText] = useState("");

  const [proposalMaxDiscount, setProposalMaxDiscount] = useState(0);
  const [initialProposalMaxDiscount, setInitialProposalMaxDiscount] = useState(0);
  const [proposalMaxDiscountError, setProposalMaxDiscountError] = useState("");

  const [serviceTaxError, setServiceTaxError] = useState("");
  const [gstTaxError, setGstTaxError] = useState("");
  const [vatTaxError, setVatTaxError] = useState("");

  useEffect(() => {
    const fetchTenantDetails = async () => {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader || !selectedTenantId) return;

      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/tenant/${selectedTenantId}/`, {
          method: "GET",
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error("Failed to fetch tenant data.");
        const data = await res.json();

        setOrgName(data.tenant_name || "");
        setDomain(data.domain_name || "");
        setCurrencyCode(data.currency_code || "");
        setInitialOrgName(data.tenant_name || "");
        setInitialDomain(data.domain_name || "");
        setInitialCurrencyCode(data.currency_code || "");

        setLogoUrl(data.tenant_logo || "");
        setInitialLogoUrl(data.tenant_logo || "");

        setAddressLine1(data.address_line1 || "");
        setAddressLine2(data.address_line2 || "");
        setCity(data.city || "");
        setState(data.state || "");
        setCountry(data.country || "");
        setZipCode(String(data.zip_code || ""));
        setInitialAddressLine1(data.address_line1 || "");
        setInitialAddressLine2(data.address_line2 || "");
        setInitialCity(data.city || "");
        setInitialState(data.state || "");
        setInitialCountry(data.country || "");
        setInitialZipCode(String(data.zip_code || ""));

        setIsServiceTax(data.is_service_tax || false);
        setIsGstTax(data.is_gst_tax || false);
        setIsVatTax(data.is_vat_tax || false);
        setServiceTaxPercentage(data.service_tax_default_percentage || 0);
        setGstTaxPercentage(data.gst_tax_default_percentage || 0);
        setVatTaxPercentage(data.vat_tax_default_percentage || 0);
        setInitialIsServiceTax(data.is_service_tax || false);
        setInitialIsGstTax(data.is_gst_tax || false);
        setInitialIsVatTax(data.is_vat_tax || false);
        setInitialServiceTaxPercentage(
          data.service_tax_default_percentage || 0
        );
        setInitialGstTaxPercentage(data.gst_tax_default_percentage || 0);
        setInitialVatTaxPercentage(data.vat_tax_default_percentage || 0);

        setTenantPhone(data.tenant_phone || "");
        setTenantEmail(data.tenant_email || "");
        setTenantTaxId(data.tenant_tax_id || "");
        setTermsAndConditions(data.terms_and_conditions || "");
        setFooterText(data.footer_text || "");
        setInitialTenantPhone(data.tenant_phone || "");
        setInitialTenantEmail(data.tenant_email || "");
        setInitialTenantTaxId(data.tenant_tax_id || "");
        setInitialTermsAndConditions(data.terms_and_conditions || "");
        setInitialFooterText(data.footer_text || "");

        setProposalMaxDiscount(data.proposal_max_discount_percentage || 0);
        setInitialProposalMaxDiscount(data.proposal_max_discount_percentage || 0);
      } catch (err) {
        console.error(err);
        await showErrorAlert("Failed to load organization settings.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenantDetails();
  }, [selectedTenantId]);

  const hasChanges = useMemo(
    () =>
      orgName.trim() !== initialOrgName.trim() ||
      domain.trim() !== initialDomain.trim() ||
      currencyCode.trim() !== initialCurrencyCode.trim() ||
      logoFile !== null ||
      logoPreview !== "" ||
      addressLine1.trim() !== initialAddressLine1.trim() ||
      addressLine2.trim() !== initialAddressLine2.trim() ||
      city.trim() !== initialCity.trim() ||
      state.trim() !== initialState.trim() ||
      country.trim() !== initialCountry.trim() ||
      String(zipCode || "").trim() !== String(initialZipCode || "").trim() ||
      isServiceTax !== initialIsServiceTax ||
      isGstTax !== initialIsGstTax ||
      isVatTax !== initialIsVatTax ||
      serviceTaxPercentage !== initialServiceTaxPercentage ||
      gstTaxPercentage !== initialGstTaxPercentage ||
      vatTaxPercentage !== initialVatTaxPercentage ||
      tenantPhone.trim() !== initialTenantPhone.trim() ||
      tenantEmail.trim() !== initialTenantEmail.trim() ||
      tenantTaxId.trim() !== initialTenantTaxId.trim() ||
      termsAndConditions.trim() !== initialTermsAndConditions.trim() ||
      footerText.trim() !== initialFooterText.trim() ||
      proposalMaxDiscount !== initialProposalMaxDiscount,
    [
      orgName,
      domain,
      currencyCode,
      initialOrgName,
      initialDomain,
      initialCurrencyCode,
      logoFile,
      logoPreview,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      zipCode,
      initialAddressLine1,
      initialAddressLine2,
      initialCity,
      initialState,
      initialCountry,
      initialZipCode,
      isServiceTax,
      isGstTax,
      isVatTax,
      serviceTaxPercentage,
      gstTaxPercentage,
      vatTaxPercentage,
      initialIsServiceTax,
      initialIsGstTax,
      initialIsVatTax,
      initialServiceTaxPercentage,
      initialGstTaxPercentage,
      initialVatTaxPercentage,
      tenantPhone,
      tenantEmail,
      tenantTaxId,
      termsAndConditions,
      footerText,
      initialTenantPhone,
      initialTenantEmail,
      initialTenantTaxId,
      initialTermsAndConditions,
      initialFooterText,
      proposalMaxDiscount,
      initialProposalMaxDiscount,
    ]
  );

  const handleDomainChange = (e: any) => {
    const val = e.target.value;
    setDomain(val);
    if (val.trim() && !DOMAIN_REGEX.test(val.trim())) {
      setDomainError("Please enter a valid domain (e.g. example.com).");
    } else {
      setDomainError("");
    }
  };

  const handleServiceTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === "" || numValue === 0) {
      setServiceTaxPercentage(0);
      setServiceTaxError("");
    } else if (numValue > 100) {
      setServiceTaxPercentage(numValue);
      setServiceTaxError("Value should not be larger than 100");
    } else {
      setServiceTaxPercentage(numValue);
      setServiceTaxError("");
    }
  };

  const handleGstTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === "" || numValue === 0) {
      setGstTaxPercentage(0);
      setGstTaxError("");
    } else if (numValue > 100) {
      setGstTaxPercentage(numValue);
      setGstTaxError("Value should not be larger than 100");
    } else {
      setGstTaxPercentage(numValue);
      setGstTaxError("");
    }
  };

  const handleVatTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === "" || numValue === 0) {
      setVatTaxPercentage(0);
      setVatTaxError("");
    } else if (numValue > 100) {
      setVatTaxPercentage(numValue);
      setVatTaxError("Value should not be larger than 100");
    } else {
      setVatTaxPercentage(numValue);
      setVatTaxError("");
    }
  };

  const handleProposalMaxDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);
    if (value === "" || numValue === 0) {
      setProposalMaxDiscount(0);
      setProposalMaxDiscountError("");
    } else if (numValue > 100) {
      setProposalMaxDiscount(numValue);
      setProposalMaxDiscountError("Value should not be larger than 100");
    } else {
      setProposalMaxDiscount(numValue);
      setProposalMaxDiscountError("");
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const filteredValue = value.replace(/[^0-9+\-]/g, "").slice(0, 15);
    setTenantPhone(filteredValue);
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 800) {
      setTermsAndConditions(value);
    }
  };

  const handleFooterTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    if (value.length <= 500) {
      setFooterText(value);
    }
  };

  const handleSave = async () => {
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader || !selectedTenantId) return;
    if (
      currentUserRole === "PLATFORM_ANALYST" ||
      currentUserRole === "SALESUSER"
    ) {
      return;
    }

    const trimmedOrg = orgName.trim();
    const trimmedDomain = domain.trim();

    if (!hasChanges) {
      await showInfoAlert("No changes made.");
      return;
    }

    if (!trimmedDomain || !DOMAIN_REGEX.test(trimmedDomain)) {
      await showErrorAlert(
        "Invalid domain format. Please enter a valid domain before saving."
      );
      return;
    }

    if (serviceTaxError !== "" || gstTaxError !== "" || vatTaxError !== "") {
      await showErrorAlert(
        "Please fix the tax percentage errors before saving."
      );
      return;
    }

    if (proposalMaxDiscountError !== "") {
      await showErrorAlert("Please fix the discount percentage errors before saving.");
      return;
    }

    try {
      setIsSaving(true);

      const formData = new FormData();

      if (trimmedOrg !== initialOrgName.trim()) {
        formData.append("tenant_name", trimmedOrg);
      }

      if (trimmedDomain !== initialDomain.trim()) {
        formData.append("domain_name", trimmedDomain);
      }

      if (currencyCode.trim() !== initialCurrencyCode.trim()) {
        formData.append("currency_code", currencyCode.trim());
      }

      if (addressLine1.trim() !== initialAddressLine1.trim()) {
        formData.append("address_line1", addressLine1.trim());
      }

      if (addressLine2.trim() !== initialAddressLine2.trim()) {
        formData.append("address_line2", addressLine2.trim());
      }

      if (city.trim() !== initialCity.trim()) {
        formData.append("city", city.trim());
      }

      if (state.trim() !== initialState.trim()) {
        formData.append("state", state.trim());
      }

      if (country.trim() !== initialCountry.trim()) {
        formData.append("country", country.trim());
      }

      if (
        String(zipCode || "").trim() !== String(initialZipCode || "").trim()
      ) {
        formData.append("zip_code", String(zipCode || "").trim());
      }

      if (isServiceTax !== initialIsServiceTax) {
        formData.append("is_service_tax", String(isServiceTax));
      }

      if (isGstTax !== initialIsGstTax) {
        formData.append("is_gst_tax", String(isGstTax));
      }

      if (isVatTax !== initialIsVatTax) {
        formData.append("is_vat_tax", String(isVatTax));
      }

      if (serviceTaxPercentage !== initialServiceTaxPercentage) {
        formData.append(
          "service_tax_default_percentage",
          String(serviceTaxPercentage)
        );
      }

      if (gstTaxPercentage !== initialGstTaxPercentage) {
        formData.append("gst_tax_default_percentage", String(gstTaxPercentage));
      }

      if (vatTaxPercentage !== initialVatTaxPercentage) {
        formData.append("vat_tax_default_percentage", String(vatTaxPercentage));
      }

      if (tenantPhone.trim() !== initialTenantPhone.trim()) {
        formData.append("tenant_phone", tenantPhone.trim());
      }

      if (tenantEmail.trim() !== initialTenantEmail.trim()) {
        formData.append("tenant_email", tenantEmail.trim());
      }

      if (tenantTaxId.trim() !== initialTenantTaxId.trim()) {
        formData.append("tenant_tax_id", tenantTaxId.trim());
      }

      if (termsAndConditions.trim() !== initialTermsAndConditions.trim()) {
        formData.append("terms_and_conditions", termsAndConditions.trim());
      }

      if (footerText.trim() !== initialFooterText.trim()) {
        formData.append("footer_text", footerText.trim());
      }

      if (proposalMaxDiscount !== initialProposalMaxDiscount) {
        formData.append("proposal_max_discount_percentage", String(proposalMaxDiscount));
      }

      if (logoFile) {
        formData.append("tenant_logo", logoFile);
      }

      const res = await fetch(`${API_BASE_URL}/tenant/${selectedTenantId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Basic ${authHeader}`,
        },
        body: formData,
      });
      if (!res.ok) throw new Error("Update failed");

      const updatedData = await res.json();

      await showSuccessAlert("Organization settings updated successfully.");

      if (trimmedOrg !== initialOrgName.trim()) {
        setInitialOrgName(trimmedOrg);
      }

      if (trimmedDomain !== initialDomain.trim()) {
        setInitialDomain(trimmedDomain);
      }

      if (currencyCode.trim() !== initialCurrencyCode.trim()) {
        setInitialCurrencyCode(currencyCode.trim());
        storeCurrencyCode(currencyCode.trim());
      }

      if (addressLine1.trim() !== initialAddressLine1.trim()) {
        setInitialAddressLine1(addressLine1.trim());
      }

      if (addressLine2.trim() !== initialAddressLine2.trim()) {
        setInitialAddressLine2(addressLine2.trim());
      }

      if (city.trim() !== initialCity.trim()) {
        setInitialCity(city.trim());
      }

      if (state.trim() !== initialState.trim()) {
        setInitialState(state.trim());
      }

      if (country.trim() !== initialCountry.trim()) {
        setInitialCountry(country.trim());
      }

      if (
        String(zipCode || "").trim() !== String(initialZipCode || "").trim()
      ) {
        setInitialZipCode(String(zipCode || "").trim());
      }

      if (isServiceTax !== initialIsServiceTax) {
        setInitialIsServiceTax(isServiceTax);
      }

      if (isGstTax !== initialIsGstTax) {
        setInitialIsGstTax(isGstTax);
      }

      if (isVatTax !== initialIsVatTax) {
        setInitialIsVatTax(isVatTax);
      }

      if (serviceTaxPercentage !== initialServiceTaxPercentage) {
        setInitialServiceTaxPercentage(serviceTaxPercentage);
      }

      if (gstTaxPercentage !== initialGstTaxPercentage) {
        setInitialGstTaxPercentage(gstTaxPercentage);
      }

      if (vatTaxPercentage !== initialVatTaxPercentage) {
        setInitialVatTaxPercentage(vatTaxPercentage);
      }

      if (tenantPhone.trim() !== initialTenantPhone.trim()) {
        setInitialTenantPhone(tenantPhone.trim());
      }

      if (tenantEmail.trim() !== initialTenantEmail.trim()) {
        setInitialTenantEmail(tenantEmail.trim());
      }

      if (tenantTaxId.trim() !== initialTenantTaxId.trim()) {
        setInitialTenantTaxId(tenantTaxId.trim());
      }

      if (termsAndConditions.trim() !== initialTermsAndConditions.trim()) {
        setInitialTermsAndConditions(termsAndConditions.trim());
      }

      if (footerText.trim() !== initialFooterText.trim()) {
        setInitialFooterText(footerText.trim());
      }

      if (proposalMaxDiscount !== initialProposalMaxDiscount) {
        setInitialProposalMaxDiscount(proposalMaxDiscount);
      }

      if (updatedData.tenant_logo) {
        setLogoUrl(updatedData.tenant_logo);
        setInitialLogoUrl(updatedData.tenant_logo);
      }

      setLogoFile(null);
      setLogoPreview("");

      if (trimmedOrg !== initialOrgName.trim()) {
        const authRespStr = localStorage.getItem("authResponse");
        if (authRespStr) {
          try {
            const authResp = JSON.parse(authRespStr);
            if (Array.isArray(authResp?.tenants)) {
              authResp.tenants = authResp.tenants.map((t: any) =>
                String(t.tenant_id) === String(selectedTenantId)
                  ? { ...t, tenant_name: trimmedOrg }
                  : t
              );
            }
            localStorage.setItem("authResponse", JSON.stringify(authResp));
            setAuth(authResp);
          } catch { }
        }
      }
    } catch (err) {
      console.error(err);
      await showErrorAlert("Failed to update organization settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setOrgName(initialOrgName);
    setDomain(initialDomain);
    setCurrencyCode(initialCurrencyCode);
    setDomainError("");
    setLogoFile(null);
    setLogoPreview("");
    setLogoUrl(initialLogoUrl);
    setAddressLine1(initialAddressLine1);
    setAddressLine2(initialAddressLine2);
    setCity(initialCity);
    setState(initialState);
    setCountry(initialCountry);
    setZipCode(String(initialZipCode || ""));
    setIsServiceTax(initialIsServiceTax);
    setIsGstTax(initialIsGstTax);
    setIsVatTax(initialIsVatTax);
    setServiceTaxPercentage(initialServiceTaxPercentage);
    setGstTaxPercentage(initialGstTaxPercentage);
    setVatTaxPercentage(initialVatTaxPercentage);
    setTenantPhone(initialTenantPhone);
    setTenantEmail(initialTenantEmail);
    setTenantTaxId(initialTenantTaxId);
    setTermsAndConditions(initialTermsAndConditions);
    setFooterText(initialFooterText);
    setProposalMaxDiscount(initialProposalMaxDiscount);
    setServiceTaxError("");
    setGstTaxError("");
    setVatTaxError("");
    setProposalMaxDiscountError("");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-8">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-4 text-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body pb-0 pt-2">
            <SettingsTabsNavigation
              activeTab="organization"
              variant="bottom"
              includeIcons={true}
              context="organization"
            />
          </div>
        </div>

        <div className="row">
          {/* Sidebar */}
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            <div className="card">
              <div className="card-body">
                <h4 className="fw-semibold mb-3">Organization Settings</h4>
                <div className="list-group list-group-flush settings-sidebar">
                  <Link to={route.companySettings} className="fw-medium active">
                    General Settings
                  </Link>
                  <Link to={route.approvalPolicy} className="fw-medium">
                    Approval Policy
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="border-bottom mb-3 pb-3">
                  <h4 className="fw-semibold mb-1">Organization Settings</h4>
                  <p>Provide the organization information below</p>
                </div>

                {isLoading ? (
                  <OrganizationLoader />
                ) : (
                  <form>
                    <div className="mb-4">
                      <div className="profile-upload">
                        <div
                          className="profile-upload-img"
                          style={{
                            backgroundImage: `url(${logoPreview || logoUrl || CompanyLogo
                              })`,
                            width: 120,
                            height: 120,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            border:
                              logoPreview || logoUrl
                                ? "none"
                                : "2px dashed #ced4da",
                          }}
                        >
                          {!!logoPreview &&
                            currentUserRole !== "PLATFORM_ANALYST" &&
                            currentUserRole !== "SALESUSER" && (
                              <button
                                type="button"
                                className="profile-remove"
                                onClick={() => {
                                  setLogoFile(null);
                                  setLogoPreview("");
                                }}
                              >
                                <i className="feather-x" />
                              </button>
                            )}
                        </div>
                        {currentUserRole !== "PLATFORM_ANALYST" &&
                          currentUserRole !== "SALESUSER" && (
                            <div className="profile-upload-content">
                              <label className="profile-upload-btn">
                                <i className="ti ti-file-broken" /> Upload Logo
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="input-img"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    setLogoFile(file);
                                    setLogoPreview(URL.createObjectURL(file));
                                  }}
                                />
                              </label>
                              <p>JPG, GIF or PNG. Max size of 800K</p>
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        <i className="ti ti-building me-2"></i>Organization
                        Details
                      </h6>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={orgName}
                            onChange={(e) => setOrgName(e.target.value)}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Domain
                          </label>
                          <input
                            type="text"
                            className={`form-control ${domainError ? "is-invalid" : ""
                              }`}
                            value={domain}
                            onChange={handleDomainChange}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                          {domainError && (
                            <div className="invalid-feedback">
                              {domainError}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Phone
                          </label>
                          <input
                            type="tel"
                            className="form-control"
                            value={tenantPhone}
                            onChange={handlePhoneChange}
                            maxLength={15}
                            placeholder="+921-545-121"
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Email
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            value={tenantEmail}
                            onChange={(e) => setTenantEmail(e.target.value)}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Tax ID
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={tenantTaxId}
                            onChange={(e) => setTenantTaxId(e.target.value)}
                            placeholder="Enter tax identification number"
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Currency Code
                          </label>
                          <CurrencySelect
                            value={currencyCode}
                            onChange={(val) => setCurrencyCode(val)}
                            isDisabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        <i className="ti ti-map-pin me-2"></i>Address
                        Information
                      </h6>
                      <div className="mb-3">
                        <label className="form-label">Address Line 1</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addressLine1}
                          onChange={(e) => setAddressLine1(e.target.value)}
                          disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Address Line 2</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addressLine2}
                          onChange={(e) => setAddressLine2(e.target.value)}
                          disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">State</label>
                          <input
                            type="text"
                            className="form-control"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Country</label>
                          <input
                            type="text"
                            className="form-control"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">ZIP Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        <i className="ti ti-file-text me-2"></i>Additional
                        Settings
                      </h6>
                      <div className="mb-3">
                        <label className="form-label">
                          Terms and Conditions
                        </label>
                        <textarea
                          className="form-control"
                          rows={6}
                          value={termsAndConditions}
                          onChange={handleTermsChange}
                          maxLength={800}
                          placeholder="Enter your organization's terms and conditions..."
                          disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                        />
                        <div className="text-end text-muted small mt-1">
                          {termsAndConditions.length}/800 characters
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Footer Text</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={footerText}
                          onChange={handleFooterTextChange}
                          maxLength={300}
                          placeholder="Enter footer text that will appear at the bottom of documents..."
                          disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                        />
                        <div className="text-end text-muted small mt-1">
                          {footerText.length}/300 characters
                        </div>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        <i className="ti ti-percentage me-2"></i>Organization
                        Tax
                      </h6>

                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <div className="card border h-100">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 className="mb-0">Service Tax</h6>
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="serviceTaxSwitch"
                                    disabled={
                                      currentUserRole === "PLATFORM_ANALYST" ||
                                      currentUserRole === "SALESUSER"
                                    }
                                    checked={isServiceTax}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setIsServiceTax(true);
                                        setIsGstTax(false);
                                        setIsVatTax(false);
                                      } else {
                                        setIsServiceTax(false);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              {isServiceTax && (
                                <div className="mt-2">
                                  <label className="form-label text-muted small">
                                    Default Percentage
                                  </label>
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      className={`form-control ${serviceTaxError ? "is-invalid" : ""
                                        }`}
                                      value={
                                        serviceTaxPercentage === 0
                                          ? ""
                                          : serviceTaxPercentage
                                      }
                                      onChange={handleServiceTaxChange}
                                      min="0"
                                      max="100"
                                      step="1"
                                      placeholder="0"
                                      disabled={
                                        currentUserRole === "PLATFORM_ANALYST" ||
                                        currentUserRole === "SALESUSER"
                                      }
                                    />
                                    <span className="input-group-text">%</span>
                                  </div>
                                  {serviceTaxError && (
                                    <div className="invalid-feedback d-block">
                                      {serviceTaxError}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4 mb-3">
                          <div className="card border h-100">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 className="mb-0">GST Tax</h6>
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="gstTaxSwitch"
                                    checked={isGstTax}
                                    disabled={
                                      currentUserRole === "PLATFORM_ANALYST" ||
                                      currentUserRole === "SALESUSER"
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setIsGstTax(true);
                                        setIsServiceTax(false);
                                        setIsVatTax(false);
                                      } else {
                                        setIsGstTax(false);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              {isGstTax && (
                                <div className="mt-2">
                                  <label className="form-label text-muted small">
                                    Default Percentage
                                  </label>
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      className={`form-control ${gstTaxError ? "is-invalid" : ""
                                        }`}
                                      value={
                                        gstTaxPercentage === 0
                                          ? ""
                                          : gstTaxPercentage
                                      }
                                      onChange={handleGstTaxChange}
                                      min="0"
                                      max="100"
                                      step="1"
                                      placeholder="0"
                                      disabled={
                                        currentUserRole === "PLATFORM_ANALYST" ||
                                        currentUserRole === "SALESUSER"
                                      }
                                    />
                                    <span className="input-group-text">%</span>
                                  </div>
                                  {gstTaxError && (
                                    <div className="invalid-feedback d-block">
                                      {gstTaxError}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4 mb-3">
                          <div className="card border h-100">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 className="mb-0">VAT Tax</h6>
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="vatTaxSwitch"
                                    checked={isVatTax}
                                    disabled={
                                      currentUserRole === "PLATFORM_ANALYST" ||
                                      currentUserRole === "SALESUSER"
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setIsVatTax(true);
                                        setIsServiceTax(false);
                                        setIsGstTax(false);
                                      } else {
                                        setIsVatTax(false);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              {isVatTax && (
                                <div className="mt-2">
                                  <label className="form-label text-muted small">
                                    Default Percentage
                                  </label>
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      className={`form-control ${vatTaxError ? "is-invalid" : ""
                                        }`}
                                      value={
                                        vatTaxPercentage === 0
                                          ? ""
                                          : vatTaxPercentage
                                      }
                                      onChange={handleVatTaxChange}
                                      min="0"
                                      max="100"
                                      step="1"
                                      placeholder="0"
                                      disabled={
                                        currentUserRole === "PLATFORM_ANALYST" ||
                                        currentUserRole === "SALESUSER"
                                      }
                                    />
                                    <span className="input-group-text">%</span>
                                  </div>
                                  {vatTaxError && (
                                    <div className="invalid-feedback d-block">
                                      {vatTaxError}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        % {getLifecycleTypeName("PROPOSAL")} Discount Limits
                      </h6>
                      <div className="row">
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Maximum Discount (%)</label>
                          <div className="input-group">
                            <input
                              type="number"
                              className={`form-control ${proposalMaxDiscountError ? "is-invalid" : ""}`}
                              value={proposalMaxDiscount === 0 ? "" : proposalMaxDiscount}
                              onChange={handleProposalMaxDiscountChange}
                              min="0"
                              max="100"
                              step="1"
                              placeholder="0"
                              disabled={currentUserRole === "PLATFORM_ANALYST" || currentUserRole === "SALESUSER"}
                            />
                            <span className="input-group-text">%</span>
                          </div>
                          {proposalMaxDiscountError && (
                            <div className="invalid-feedback d-block">{proposalMaxDiscountError}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
                      <div className="mt-4">
                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2"
                          onClick={(e) => {
                            handleCancel();
                            e.currentTarget.blur();
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary me-2"
                          onClick={(e) => {
                            handleSave();
                            e.currentTarget.blur();
                          }}
                          disabled={
                            isSaving ||
                            !!domainError ||
                            !hasChanges ||
                            !!serviceTaxError ||
                            !!gstTaxError ||
                            !!vatTaxError ||
                            !!proposalMaxDiscountError
                          }
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
